import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { BillingInformation, useBillingInformationCheck } from '@app/api';

import { Chip } from '@app/core';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Address } from './Address';
import { billingInformationToString } from './billingInformationToString';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  paneContentAddress: {
    flex: 1,
  },
  address: {
    backgroundColor: theme.colors.white,
  },
  titleLine: {
    flex: 1,
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.marginBase,
  },
  title: {
    ...theme.fonts.h5,
    marginBottom: theme.marginBase,
  },
  loader: {
    display: 'inline-block',
    animation: '$spin .5s linear infinite',
    marginRight: theme.marginBase / 2,
  },
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
}));

interface Props {
  billingInformation: BillingInformation;
}

enum VerificationStatus {
  SUCCESS = 'SUCCESS',
  INVALID = 'INVALID',
  EMPTY = 'EMPTY',
  PENDING = 'PENDING',
}

const chipColor: Record<VerificationStatus, Colors> = {
  [VerificationStatus.SUCCESS]: 'green',
  [VerificationStatus.INVALID]: 'red',
  [VerificationStatus.EMPTY]: 'ink',
  [VerificationStatus.PENDING]: 'blue',
};

export const BillingInformationVerificationModalPane = ({
  billingInformation,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data, isLoading } = useBillingInformationCheck(billingInformation.id);

  const vatString = useMemo(() => {
    const vat = data?.vat;
    if (!vat) {
      return '';
    }
    return [vat.name, vat.address].filter(Boolean).join('\n');
  }, [data?.vat]);

  const siretString = useMemo(() => {
    const siret = data?.siret;
    if (!siret) {
      return '';
    }
    return [
      siret.name,
      siret.address?.line1,
      siret.address?.line2,
      [siret.address?.postalCode, siret.address?.city, siret.address?.country]
        .filter(Boolean)
        .join(' '),
    ]
      .filter(Boolean)
      .join('\n');
  }, [data?.siret]);

  const vatStatus: VerificationStatus = useMemo(() => {
    if (!billingInformation.vatNumber) {
      return VerificationStatus.EMPTY;
    }

    if (isLoading || !data?.vat) {
      return VerificationStatus.PENDING;
    }

    if (!data.vat.success) {
      return VerificationStatus.INVALID;
    }

    return VerificationStatus.SUCCESS;
  }, [isLoading, billingInformation, data?.vat]);

  const siretStatus: VerificationStatus = useMemo(() => {
    if (!billingInformation.routingId) {
      return VerificationStatus.EMPTY;
    }

    if (isLoading || !data?.siret) {
      return VerificationStatus.PENDING;
    }

    if (!data.siret.success) {
      return VerificationStatus.INVALID;
    }

    return VerificationStatus.SUCCESS;
  }, [isLoading, billingInformation, data?.siret]);

  return (
    <>
      <div className={classes.paneContentAddress}>
        <div className={classes.title}>
          {t('admin.billing.billingInfo.addressTitle')}
        </div>
        <Address className={classes.address}>
          {billingInformationToString(billingInformation)}
        </Address>
      </div>
      <div>
        <div className={classes.titleLine}>
          <div className={classes.title}>
            {t('admin.billing.billingInfo.vatTitle')}
          </div>
          <Chip overGray color={chipColor[vatStatus]}>
            {vatStatus === VerificationStatus.EMPTY && 'EMPTY'}
            {vatStatus === VerificationStatus.PENDING && (
              <>
                <i className={classNames('ri-loader-4-line', classes.loader)} />
                {t('admin.billing.billingInfo.loading')}
              </>
            )}
            {data?.vat?.message}
          </Chip>
        </div>
        {vatString && (
          <Address className={classes.address}>{vatString}</Address>
        )}
      </div>
      <div>
        <div className={classes.titleLine}>
          <div className={classes.title}>
            {t('admin.billing.billingInfo.siretTitle')}
          </div>
          <Chip overGray color={chipColor[siretStatus]}>
            {siretStatus === VerificationStatus.EMPTY && 'EMPTY'}
            {siretStatus === VerificationStatus.PENDING && (
              <>
                <i className={classNames('ri-loader-4-line', classes.loader)} />
                {t('admin.billing.billingInfo.loading')}
              </>
            )}
            {data?.siret?.message}
          </Chip>
        </div>
        {vatString && (
          <Address className={classes.address}>{siretString}</Address>
        )}
      </div>
    </>
  );
};
